import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Snackbar, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../components/layout/Breadcrumb';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { TestNotFound } from '../../../components/shared/TestNotFound';
import { ROUTES } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';
import { useRegisterUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { getPointLoadBatch, savePointLoadBatch } from '../../../services/pointLoadTestService';
import { downloadReport } from '../../../services/reportService';
import type { PltInputFields, PointLoadBatchContext } from '../../../types/pointLoadTest';
import { previewPointLoad, isValidValidity } from './computations';
import { emptyPltInput, PLT_K_FACTOR_DEFAULT, PLT_LOAD_AREA_DEFAULT_M2 } from './pointLoadFields';
import { PLTBatchGrid, type PltGridRow } from './PLTBatchGrid';
import { EvidenceNagLine } from '../../../components/evidence/EvidenceNagLine';
import { evidenceKey } from '../../../constants/evidenceFields';
import { fetchFieldEvidenceSummary } from '../../../services/observationMediaService';
import { TestActionBar } from '../components/TestActionBar';
import { useReportPreview } from '../../../components/reportPreview/useReportPreview';
import { ObservationMediaPanel } from '../../../components/observations/ObservationMediaPanel';

type RowFilter = 'all' | 'incomplete' | 'complete' | 'invalid';

const EMPTY_CONTEXT: PointLoadBatchContext = {
  workOrderKey: '', workOrderId: '', projectId: '', projectName: '', siteName: '', clientName: '',
  labLocation: 'Fredericton', reportUnit: 'metric', testName: 'Point Load Test',
  testedBy: null, testDate: null, reviewedBy: null, reviewDate: null, approvedBy: null,
  approvalDate: null, testStatus: '',
};

/** Build an editable input record from a stored PLT row (program defaults applied). */
function toInput(data: PltGridRow['input'] | null | undefined): PltInputFields {
  if (!data) return emptyPltInput();
  const base = emptyPltInput();
  return {
    testType: data.testType ?? base.testType,
    loadDirection: data.loadDirection ?? base.loadDirection,
    validity: data.validity ?? base.validity,
    unit: data.unit ?? null,
    gaugePsi: data.gaugePsi ?? null,
    loadAreaM2: data.loadAreaM2 ?? base.loadAreaM2,
    widthMm: data.widthMm ?? null,
    platenDistanceMm: data.platenDistanceMm ?? null,
    platenDistanceFailureMm: data.platenDistanceFailureMm ?? null,
    sampleLengthMm: data.sampleLengthMm ?? null,
    kFactor: data.kFactor ?? base.kFactor,
    notes: data.notes ?? null,
    additionalObservations: data.additionalObservations ?? null,
  };
}

const rowComplete = (input: PltInputFields): boolean => {
  const c = previewPointLoad(input);
  return input.gaugePsi !== null && c.is50Mpa !== null;
};

export const RockPLTWorkflowPage = () => {
  const { projectId, workOrderId, workOrderDetailKey } = useParams<{
    projectId: string; workOrderId: string; workOrderDetailKey: string;
  }>();
  const navigate = useNavigate();
  const { openPreview, openHistory, previewDialog } = useReportPreview(workOrderDetailKey);
  const theme = useTheme();
  const { canEditTestData, canSaveTestData } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [filter, setFilter] = useState<RowFilter>('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'borehole' | 'sampleId'>('borehole');

  const [context, setContext] = useState<PointLoadBatchContext>(EMPTY_CONTEXT);
  const [rows, setRows] = useState<PltGridRow[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  // PLT is a batch page — media evidence is per-sample, so the panel is bound
  // to a user-selected row (defaults to the sample the page was opened from).
  const [mediaWodKey, setMediaWodKey] = useState<string>('');
  // Field evidence (Gauge PSI, per sample): one batched summary call powers
  // every row's camera-button state + the header nag.
  const [evidenceHas, setEvidenceHas] = useState<Record<string, boolean>>({});

  const loadData = useCallback(async () => {
    if (!workOrderDetailKey) return;
    setLoading(true);
    try {
      const data = await getPointLoadBatch(workOrderDetailKey);
      setContext(data.context);
      setRows(data.rows.map((r) => {
        const input = toInput(r.data);
        return {
          workOrderDetailKey: r.workOrderDetailKey,
          sampleId: r.sampleId,
          borehole: r.borehole,
          depthTop: r.depthTop,
          depthBase: r.depthBase,
          input,
          computed: previewPointLoad(input),
        };
      }));
      setDirty(false);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) setNotFound(true);
      else setSnackbar({ open: true, message: 'Failed to load Point Load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [workOrderDetailKey]);

  useEffect(() => { void loadData(); }, [loadData]);

  // BUG-2026-0189 — warn on browser refresh / close / back with unsaved edits.
  useUnsavedChangesWarning(dirty);

  useEffect(() => {
    if (rows.length === 0) { setEvidenceHas({}); return; }
    let cancelled = false;
    const gaugeKey = evidenceKey('pointLoad', 'gaugePsi');
    fetchFieldEvidenceSummary(rows.map((r) => r.workOrderDetailKey))
      .then((summary) => {
        if (cancelled) return;
        const has: Record<string, boolean> = {};
        summary.forEach((s) => { if (s.FieldKey === gaugeKey) has[s.WorkOrderDetailKey.toLowerCase()] = true; });
        setEvidenceHas(has);
      })
      .catch(() => { /* buttons degrade to "missing" until opened */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  const handleEvidenceChange = useCallback((wodKey: string, has: boolean) => {
    setEvidenceHas((prev) => ({ ...prev, [wodKey.toLowerCase()]: has }));
  }, []);

  const handleCellChange = useCallback((
    wodKey: string,
    field: keyof PltInputFields,
    value: string | number | null,
  ) => {
    setRows((prev) => prev.map((r) => {
      if (r.workOrderDetailKey !== wodKey) return r;
      const input = { ...r.input, [field]: value } as PltInputFields;
      return { ...r, input, computed: previewPointLoad(input) };
    }));
    setDirty(true);
  }, []);

  /** Bulk-apply a program default (K factor / load area) to every row. */
  const applyToAll = useCallback((field: 'kFactor' | 'loadAreaM2', value: number) => {
    setRows((prev) => prev.map((r) => {
      const input = { ...r.input, [field]: value };
      return { ...r, input, computed: previewPointLoad(input) };
    }));
    setDirty(true);
  }, []);

  // BUG-2026-0189 — returns true only when the batch actually persisted, so the
  // unsaved-changes guard can decide whether to leave. Callers that ignore the
  // result (buttons) are unaffected.
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!workOrderDetailKey) return false;
    setSaving(true);
    try {
      const payload = rows.map((r) => ({ workOrderDetailKey: r.workOrderDetailKey, data: r.input }));
      const data = await savePointLoadBatch(workOrderDetailKey, payload);
      setContext(data.context);
      setRows(data.rows.map((r) => {
        const input = toInput(r.data);
        return {
          workOrderDetailKey: r.workOrderDetailKey,
          sampleId: r.sampleId, borehole: r.borehole, depthTop: r.depthTop, depthBase: r.depthBase,
          input, computed: previewPointLoad(input),
        };
      }));
      setDirty(false);
      setSnackbar({ open: true, message: 'Saved', severity: 'success' });
      return true;
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Save failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  }, [workOrderDetailKey, rows]);

  // BUG-2026-0189 — register the shared unsaved-changes guard so every in-app
  // exit path (nav, logo, breadcrumbs, browser Back, "Back to Work Order")
  // prompts Save & Leave / Discard & Leave / Cancel. PLT is a single batch grid
  // (its top tabs are row filters, not data tabs), so there is no tab auto-save.
  const requestLeave = useRegisterUnsavedGuard({ dirty, save: () => handleSave(), setConfirm: setConfirmDlg });

  const handleGenerateReport = useCallback(async () => {
    if (!workOrderDetailKey) return;
    setReportGenerating(true);
    try {
      await downloadReport(workOrderDetailKey);
      setSnackbar({ open: true, message: 'Report downloaded', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Report generation failed', severity: 'error' });
    } finally {
      setReportGenerating(false);
    }
  }, [workOrderDetailKey]);

  const handleBack = () => {
    const goBack = () => {
      if (projectId && workOrderId) {
        navigate(ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', workOrderId));
      }
    };
    // BUG-2026-0189 — shared Save & Leave / Discard & Leave / Cancel dialog.
    requestLeave(goBack);
  };

  const counts = useMemo(() => {
    let incomplete = 0, complete = 0, invalid = 0;
    for (const r of rows) {
      if (!isValidValidity(r.input.validity)) invalid += 1;
      else if (rowComplete(r.input)) complete += 1;
      else incomplete += 1;
    }
    return { all: rows.length, incomplete, complete, invalid };
  }, [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      const passFilter =
        filter === 'all' ? true
        : filter === 'invalid' ? !isValidValidity(r.input.validity)
        : filter === 'complete' ? isValidValidity(r.input.validity) && rowComplete(r.input)
        : isValidValidity(r.input.validity) && !rowComplete(r.input);
      const passSearch = !q
        || (r.sampleId ?? '').toLowerCase().includes(q)
        || (r.borehole ?? '').toLowerCase().includes(q);
      return passFilter && passSearch;
    });
    return filtered.sort((a, b) => {
      if (sortBy === 'sampleId') return (a.sampleId ?? '').localeCompare(b.sampleId ?? '');
      const bh = (a.borehole ?? '').localeCompare(b.borehole ?? '');
      return bh !== 0 ? bh : (a.depthTop ?? 0) - (b.depthTop ?? 0);
    });
  }, [rows, filter, search, sortBy]);

  const handleValidate = useCallback(() => {
    const missingLoad = rows.filter((r) => isValidValidity(r.input.validity) && r.input.gaugePsi === null).length;
    const msg = counts.invalid + missingLoad === 0
      ? `All ${counts.all} samples valid and ready.`
      : `${counts.invalid} invalid, ${counts.incomplete} incomplete (${missingLoad} missing gauge reading).`;
    setSnackbar({ open: true, message: msg, severity: counts.invalid + missingLoad === 0 ? 'success' : 'error' });
  }, [rows, counts]);

  /** Re-run the calculation preview on every row (e.g. after changing defaults). */
  const handleProcessCalculations = useCallback(() => {
    setRows((prev) => prev.map((r) => ({ ...r, computed: previewPointLoad(r.input) })));
    setSnackbar({ open: true, message: 'Calculations processed', severity: 'success' });
  }, []);

  const handleExportCsv = useCallback(() => {
    const head = ['Borehole', 'Sample ID', 'From (m)', 'To (m)', 'Test Type', 'Direction', 'Validity', 'Unit',
      'Gauge PSI', 'W (mm)', 'D (mm)', "D' (mm)", 'L (mm)', 'Load Area', 'K',
      'Code', 'Validity Code', 'Gauge kPa', 'Load (kN)', 'De', 'De2', 'Is', 'F', 'Is(50)', 'UCS'];
    const lines = [head.join(',')];
    for (const r of rows) {
      const i = r.input, c = r.computed;
      const cells = [r.borehole, r.sampleId, r.depthTop, r.depthBase, i.testType, i.loadDirection, i.validity, i.unit,
        i.gaugePsi, i.widthMm, i.platenDistanceMm, i.platenDistanceFailureMm, i.sampleLengthMm, i.loadAreaM2, i.kFactor,
        c.testTypeDirectionCode, c.validityCode, c.gaugeKpa, c.failureLoadKn, c.deMm, c.de2Mm2, c.isMpa, c.sizeCorrectionF, c.is50Mpa, c.ucsMpa];
      lines.push(cells.map((v) => (v === null || v === undefined ? '' : `"${String(v).replace(/"/g, '""')}"`)).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PLT_${context.workOrderId || 'work-order'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, context.workOrderId]);

  const breadcrumbs = [
    { label: context.clientName || '...', path: ROUTES.CLIENTS, entityType: 'client' as const },
    { label: context.projectName || context.projectId || '...', path: ROUTES.PROJECTS, entityType: 'project' as const },
    { label: context.siteName || '...', entityType: 'site' as const },
    { label: context.workOrderId || '...',
      path: projectId && workOrderId && !workOrderId.startsWith(':')
        ? ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', workOrderId)
        : undefined,
      entityType: 'workOrder' as const },
    { label: 'Point Load Test', entityType: 'test' as const },
  ];

  if (notFound) return <TestNotFound entity="test" />;
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const FILTER_TABS: { key: RowFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'incomplete', label: 'Incomplete', count: counts.incomplete },
    { key: 'complete', label: 'Complete', count: counts.complete },
    { key: 'invalid', label: 'Invalid', count: counts.invalid },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header: breadcrumb + status | actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Breadcrumb items={breadcrumbs} />
          {context.testStatus && (
            <Chip label={context.testStatus} size="small"
              color={context.testStatus === 'Approved' ? 'success' : context.testStatus === 'In Progress' ? 'warning' : 'default'}
              sx={{ fontSize: '0.7rem', height: 22 }} />
          )}
        </Box>
        {previewDialog}
        <TestActionBar
          canSave={canSaveTestData}
          onSave={handleSave}
          saving={saving}
          dirty={dirty}
          onPrint={() => window.print()}
          onGenerateReport={handleGenerateReport}
          onPreviewReport={openPreview}
          onReportHistory={openHistory}
          reportGenerating={reportGenerating}
          pagerLabel={`${rows.length} samples`}
        />
      </Box>

      {/* Title + action toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Point Load Test — Result Entry</Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {rows.length} selected samples pre-loaded
        </Typography>
        {/* Passive evidence nag — Gauge PSI photos, never a popup or save blocker */}
        <EvidenceNagLine
          count={rows.filter((r) => !evidenceHas[r.workOrderDetailKey.toLowerCase()]).length}
          noun="sample"
          tooltip="Samples without a Gauge PSI evidence photo — use the camera button next to each gauge reading"
        />
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleValidate}>Validate Results</Button>
          <Button size="small" variant="outlined" disabled={!canEditTestData} onClick={handleProcessCalculations}>Process Calculations</Button>
          <Button size="small" variant="contained" disabled={!canSaveTestData || saving} onClick={handleSave}
            sx={{ backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.85) } }}>
            {saving ? 'Saving…' : 'Save Results'}
          </Button>
        </Box>
      </Box>

      {/* Filter tabs + search/sort */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 1.25,
        flexWrap: 'wrap', borderBottom: `2px solid ${theme.palette.divider}` }}>
        <Tabs
          value={filter}
          onChange={(_, v) => setFilter(v as RowFilter)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 42,
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
            '& .MuiTab-root': {
              minHeight: 42, py: 0.5, px: 2.25, textTransform: 'none', fontWeight: 700,
              fontSize: '0.9rem', color: theme.palette.text.secondary,
            },
            '& .Mui-selected': { color: `${theme.palette.primary.main} !important` },
          }}
        >
          {FILTER_TABS.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={(
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {t.label}
                  <Box component="span" sx={{
                    minWidth: 22, px: 0.75, py: 0.1, borderRadius: 999, fontSize: '0.72rem', fontWeight: 800,
                    backgroundColor: filter === t.key ? alpha(theme.palette.primary.main, 0.16) : alpha(theme.palette.text.primary, 0.08),
                    color: filter === t.key ? theme.palette.primary.dark : theme.palette.text.secondary,
                  }}>{t.count}</Box>
                </Box>
              )}
            />
          ))}
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.5 }}>
          <TextField size="small" placeholder="Search sample / borehole" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ width: 220 }} autoComplete="off"
            inputProps={{ autoComplete: 'off', style: { fontSize: '0.8rem', padding: '6px 8px' } }} />
          <TextField select size="small" value={sortBy} onChange={(e) => setSortBy(e.target.value as 'borehole' | 'sampleId')}
            sx={{ width: 180 }} inputProps={{ style: { fontSize: '0.8rem' } }}>
            <MenuItem value="borehole" sx={{ fontSize: '0.8rem' }}>Sort by borehole + depth</MenuItem>
            <MenuItem value="sampleId" sx={{ fontSize: '0.8rem' }}>Sort by sample ID</MenuItem>
          </TextField>
          <Button size="small" variant="text" disabled={!canEditTestData} onClick={() => applyToAll('kFactor', PLT_K_FACTOR_DEFAULT)}>
            K={PLT_K_FACTOR_DEFAULT} all
          </Button>
          <Button size="small" variant="text" disabled={!canEditTestData} onClick={() => applyToAll('loadAreaM2', PLT_LOAD_AREA_DEFAULT_M2)}>
            Area={PLT_LOAD_AREA_DEFAULT_M2} all
          </Button>
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <PLTBatchGrid
          rows={visibleRows}
          onChange={handleCellChange}
          readOnly={!canEditTestData}
          evidenceHas={evidenceHas}
          onEvidenceChange={handleEvidenceChange}
        />
      </Box>

      {/* Additional Observations media — lab-internal, per-sample */}
      {rows.length > 0 && (
        <Box sx={{ mt: 1, p: 1.25, border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Additional Observations (internal — not printed on the report)
            </Typography>
            <TextField
              select size="small" label="Sample" sx={{ width: 260 }}
              value={mediaWodKey || workOrderDetailKey || ''}
              onChange={(e) => setMediaWodKey(e.target.value)}
              inputProps={{ style: { fontSize: '0.8rem' } }}
            >
              {rows.map((r) => (
                <MenuItem key={r.workOrderDetailKey} value={r.workOrderDetailKey} sx={{ fontSize: '0.8rem' }}>
                  {[r.borehole, r.sampleId].filter(Boolean).join(' — ') || r.workOrderDetailKey.substring(0, 8)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {(() => {
            const selWod = mediaWodKey || workOrderDetailKey || rows[0].workOrderDetailKey;
            const selRow = rows.find((r) => r.workOrderDetailKey === selWod);
            return (
              <>
                <TextField
                  size="small" fullWidth multiline minRows={2}
                  placeholder="Internal observations for this sample — saved with Save Results, never printed."
                  value={selRow?.input.additionalObservations ?? ''}
                  onChange={(e) => handleCellChange(selWod, 'additionalObservations', e.target.value || null)}
                  disabled={!canEditTestData}
                  inputProps={{ style: { fontSize: '0.8rem' } }}
                  sx={{ mb: 0.5 }}
                />
                <ObservationMediaPanel workOrderDetailKey={selWod} readOnly={!canEditTestData} />
              </>
            );
          })()}
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, color: theme.palette.text.secondary }}>
        <Typography variant="caption">{rows.length} samples pre-loaded from the work order selection.</Typography>
        <Typography variant="caption">No stages. No bulk upload.</Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button size="small" onClick={handleExportCsv} sx={{ color: theme.palette.text.secondary }}>⬇ Export CSV</Button>
          <Button size="small" onClick={handleBack} sx={{ color: theme.palette.text.secondary }}>Back to Work Order</Button>
          <Button size="small" variant="contained" disabled={!canSaveTestData || saving}
            onClick={handleSave}
            sx={{ backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.85) } }}>
            {saving ? 'Saving…' : 'Save Results'}
          </Button>
        </Box>
      </Box>

      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
